// FILE: src/components/case-studies/admin/caseAdminUtils.js
//
// Pure logic for the case-study authoring UI (Case Study Question Framework,
// PR 2). Everything here is side-effect free and unit-tested; the components
// call these to compute RPC payloads, so sequencing rules live in ONE place.

import { CASE_STUDY_TAGS } from '../../../config/examCategories';
import { validateImportRow } from '../../../services/importService';

/**
 * Normalize an admin_get_case_full payload into a stage-keyed model:
 *   [{ ...stage, sections: [], artifacts: [], questions: [] }]  (all sorted)
 */
export function buildStageModel(full) {
  const stages = [...(full.stages || [])].sort((a, b) => a.sequence - b.sequence);
  return stages.map((st) => ({
    ...st,
    sections: (full.sections || [])
      .filter((s) => s.stage_id === st.id)
      .sort((a, b) => a.sequence - b.sequence),
    artifacts: (full.artifacts || [])
      .filter((a) => a.stage_id === st.id)
      .sort((a, b) => a.sequence - b.sequence),
    questions: (full.questions || [])
      .filter((q) => q.case_stage_id === st.id)
      .sort((a, b) => (a.case_sequence || 0) - (b.case_sequence || 0)),
  }));
}

/**
 * Compute the FULL question layout (admin_reorder_case_questions payload)
 * after moving one question to a target stage/position.
 *
 * - Global case_sequence is always renumbered 1..N walking stages in order,
 *   so the result can never contain duplicates or stage/sequence conflicts.
 * - `position` is the 0-based index within the target stage's question list
 *   (clamped); omit to append.
 */
export function computeMovePayload(stageModel, questionId, targetStageId, position) {
  const lists = stageModel.map((st) => ({
    stage_id: st.id,
    questions: st.questions.map((q) => q.id).filter((id) => id !== questionId),
  }));
  const target = lists.find((l) => l.stage_id === targetStageId);
  if (!target) throw new Error(`Unknown target stage ${targetStageId}`);
  const pos = position === undefined || position === null
    ? target.questions.length
    : Math.max(0, Math.min(position, target.questions.length));
  target.questions.splice(pos, 0, questionId);
  return renumber(lists);
}

/** Reorder payload for a swap within one stage (move up/down). */
export function computeSwapPayload(stageModel, stageId, indexA, indexB) {
  const lists = stageModel.map((st) => ({
    stage_id: st.id,
    questions: st.questions.map((q) => q.id),
  }));
  const target = lists.find((l) => l.stage_id === stageId);
  if (!target || indexA < 0 || indexB < 0 ||
      indexA >= target.questions.length || indexB >= target.questions.length) {
    throw new Error('Invalid swap');
  }
  const t = target.questions[indexA];
  target.questions[indexA] = target.questions[indexB];
  target.questions[indexB] = t;
  return renumber(lists);
}

function renumber(lists) {
  const out = [];
  let seq = 1;
  lists.forEach((l) => l.questions.forEach((qid) => {
    out.push({ question_id: qid, stage_id: l.stage_id, case_sequence: seq });
    seq += 1;
  }));
  return out;
}

/** True when every case_sequence in a payload is unique (sanity guard). */
export function hasUniqueSequences(payload) {
  const seqs = payload.map((p) => p.case_sequence);
  return new Set(seqs).size === seqs.length;
}

/** Stage-reorder payload after moving stage at `index` by `delta` (+1/-1). */
export function computeStageMovePayload(stageModel, index, delta) {
  const ids = stageModel.map((s) => s.id);
  const j = index + delta;
  if (j < 0 || j >= ids.length) return null;
  const t = ids[index]; ids[index] = ids[j]; ids[j] = t;
  return ids.map((stage_id, i) => ({ stage_id, sequence: i + 1 }));
}

/** A stage is deletable only when no questions are linked to it. */
export function stageDeletionBlockers(stage) {
  const blockers = [];
  if (stage.questions.length > 0) {
    blockers.push(`${stage.questions.length} linked question(s) — move or unlink them first`);
  }
  return blockers;
}

/** Advisory (never blocking) warnings: unknown thematic tags. */
export function tagWarnings(tags) {
  return (tags || [])
    .filter((t) => !CASE_STUDY_TAGS.includes(t))
    .map((t) => `Tag "${t}" is not in the curated vocabulary (advisory only — publishing is not blocked).`);
}

/**
 * Build the LEARNER-SAFE preview payload from admin editor data, shaped like
 * get_case_runtime / the attempt snapshot ({case, stages:[...]}) so the
 * preview consumes what the learner runner will consume (PR 3).
 *
 * Strips: correct_answers, explanation, author_notes, source_reference,
 * review/status metadata. The editor NEVER touches case_attempt_answer_keys;
 * this is derived purely from admin-authorized question rows.
 */
export function buildLearnerPreview(full) {
  const model = buildStageModel(full);
  const cs = full.case || {};
  return {
    case: {
      id: cs.id, slug: cs.slug, certification: cs.certification,
      title: cs.title, summary: cs.summary, learner_role: cs.learner_role,
      delivery_approach: cs.delivery_approach, difficulty: cs.difficulty,
      estimated_minutes: cs.estimated_minutes, version: cs.version,
    },
    stages: model.map((st) => ({
      id: st.id, sequence: st.sequence, title: st.title, intro: st.intro,
      sections: st.sections.map((s) => ({
        id: s.id, section_type: s.section_type, title: s.title,
        content: s.content, sequence: s.sequence,
      })),
      artifacts: st.artifacts.map((a) => ({
        id: a.id, artifact_type: a.artifact_type, title: a.title,
        description: a.description, content_json: a.content_json,
        asset_url: a.asset_url, alt_text: a.alt_text, sequence: a.sequence,
      })),
      questions: st.questions.map((q) => ({
        id: q.id, case_sequence: q.case_sequence, type: q.type,
        question: q.question, choices: q.choices, image: q.image || null,
      })),
    })),
  };
}

/** Shallow dirty check for the overview form. */
export function isOverviewDirty(form, original) {
  const fields = ['slug', 'title', 'summary', 'learner_role', 'delivery_approach',
    'difficulty', 'estimated_minutes', 'source_reference', 'author_notes', 'status'];
  if (fields.some((f) => String(form[f] ?? '') !== String(original[f] ?? ''))) return true;
  const a = (form.tags || []).join('|');
  const b = (original.tags || []).join('|');
  return a !== b;
}

/**
 * Client-side pre-validation for a pm-pixel-case-bundle@1 (the server
 * re-validates atomically; this exists to give actionable feedback before
 * the RPC call). Embedded questions are checked with the SAME validator the
 * Bulk Import screen uses, so the two paths cannot drift.
 * Returns { errors: [], warnings: [], summary: {stages, sections, artifacts, questions} }.
 */
// Mirror of the case_study_artifacts_artifact_type_check constraint (migration
// 070). The importer rejects any other type, so surface it here first.
const ALLOWED_ARTIFACT_TYPES = new Set(['text', 'table', 'email', 'memo',
  'status_report', 'risk_register', 'stakeholder_matrix', 'backlog', 'chart',
  'image', 'organization_chart', 'transcript', 'contract_excerpt']);

export function validateCaseBundle(bundle) {
  const errors = [];
  const warnings = [];
  const summary = { stages: 0, sections: 0, artifacts: 0, questions: 0 };
  if (!bundle || typeof bundle !== 'object') {
    return { errors: ['Bundle is not a JSON object.'], warnings, summary };
  }
  if (bundle.format !== 'pm-pixel-case-bundle@1') {
    errors.push(`Unsupported format "${bundle.format}" (expected pm-pixel-case-bundle@1).`);
  }
  const cs = bundle.case || {};
  if (!cs.slug || !String(cs.slug).trim()) errors.push('case.slug is required.');
  if (!cs.certification) errors.push('case.certification is required.');
  warnings.push(...tagWarnings(cs.tags || []));

  if (!Array.isArray(bundle.stages) || bundle.stages.length === 0) {
    errors.push('Bundle needs a non-empty stages array.');
    return { errors, warnings, summary };
  }
  const refs = new Set();
  const seqs = new Set();
  const seenQ = new Set();
  bundle.stages.forEach((st, si) => {
    summary.stages += 1;
    const where = `Stage ${st.sequence ?? si + 1}`;
    (st.sections || []).forEach(() => { summary.sections += 1; });
    (st.artifacts || []).forEach((a) => {
      summary.artifacts += 1;
      if (!a.ref) errors.push(`${where}: artifact "${a.title || '?'}" is missing its bundle ref.`);
      else if (refs.has(a.ref)) errors.push(`Duplicate artifact ref "${a.ref}".`);
      else refs.add(a.ref);
      if (!ALLOWED_ARTIFACT_TYPES.has(a.artifact_type)) {
        errors.push(`${where}: artifact "${a.title || a.ref}" has type "${a.artifact_type}" — the importer only accepts: ${[...ALLOWED_ARTIFACT_TYPES].join(', ')}.`);
      }
      const isImage = ['image', 'organization_chart', 'chart'].includes(a.artifact_type);
      if (isImage && a.asset_url && !(a.alt_text || '').trim()) {
        errors.push(`${where}: image artifact "${a.title || a.ref}" needs alt_text.`);
      }
    });
    (st.questions || []).forEach((q) => {
      summary.questions += 1;
      const qp = q.question || {};
      if (q.case_sequence === undefined || q.case_sequence === null) {
        errors.push(`${where}: question ${qp.id || '?'} needs a case_sequence.`);
      } else if (seqs.has(q.case_sequence)) {
        errors.push(`Duplicate case_sequence ${q.case_sequence}.`);
      } else seqs.add(q.case_sequence);
      if (!qp.id) { errors.push(`${where}: every question needs question.id.`); return; }
      // Same validator as Bulk Import. Existing-id rows will be linked as-is
      // server-side, so validation failures for them are advisory only.
      const r = validateImportRow(qp, seenQ, new Set(), { examId: cs.certification });
      seenQ.add(String(qp.id));
      r.errors.forEach((msg) => warnings.push(`${qp.id}: ${msg} (blocking only if ${qp.id} does not already exist in the bank)`));
      (q.artifact_refs || []).forEach((ar) => {
        if (!refs.has(ar.ref)) {
          errors.push(`${qp.id} references artifact ref "${ar.ref}" which is not defined in this or an earlier stage.`);
        }
      });
    });
  });
  return { errors, warnings, summary };
}

/** Trigger a client-side JSON download (export button). */
export function downloadJson(filename, data) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
